import React, { useEffect, useRef, useState } from "react";
/** helpers */
import clsx from "clsx";
import { useTranslation } from "react-i18next";

/** redux */
import { getShowIDs } from "@/src/_appStore/Common";
import { getScrolled } from "@/src/_appStore/DB_Indicators";
import { useSelector } from "react-redux";

/** MUI */
import { alpha, useMediaQuery, Box, IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
/** - TREE */
import {
    TreeItem2Checkbox,
    TreeItem2Content,
    TreeItem2GroupTransition,
    TreeItem2IconContainer,
    TreeItem2Label,
    TreeItem2Root,
} from "@mui/x-tree-view/TreeItem2";
import { TreeItem2Icon } from "@mui/x-tree-view/TreeItem2Icon";
import { TreeItem2Provider } from "@mui/x-tree-view/TreeItem2Provider";
import { unstable_useTreeItem2 as useTreeItem2 } from "@mui/x-tree-view/useTreeItem2";
/** - icons */
import { AddCircleOutline, InfoOutlined, MoreVert } from "@mui/icons-material";

/** UI */
import AppTooltip from "@/src/_common/AppTooltip";

const CustomTreeItemContent = styled(TreeItem2Content)(({ theme }) => ({
    padding: theme.spacing(0.5, 1),
    width: "auto",
}));

const Silent = (props) => {
    const { rootRef, children } = props;

    const handleKeyDown = (event) => {
        if (event?.keyCode === 27) {
            if (rootRef?.current) {
                rootRef?.current?.focus();
            }
        }

        if (event?.keyCode === 13 || event?.keyCode === 32) {
            const _target = event.target;
            event?.preventDefault();
            event?.stopPropagation();
            if (_target?.nodeName === "BUTTON" && event?.keyCode === 13) {
                _target.click();
            }
        }
    };

    return (
        <Box onKeyDown={handleKeyDown} sx={{ display: "flex", alignItems: "flex-start", pt: 0.75 }}>
            {children}
        </Box>
    );
};

const useStyles = makeStyles((theme) => ({
    root: {
        marginRight: 0,
    },
    content: {
        [theme.breakpoints.down("md")]: {
            padding: theme.spacing(0.25, 0.25),
            flexGrow: 1,
        },
    },
    label: {
        fontSize: "1rem",
        flexGrow: 1,
        display: "flex",
        gap: 1,
        lineHeight: 1.33,
        [theme.breakpoints.down("md")]: {
            // fontSize: "0.77777778rem",
            fontSize: "0.88888889rem",
        },
    },
    labelAccent: {
        fontWeight: 600,
    },
    labelSelected: {
        color: "#000",
    },
    expanded: {},
    selected: {
        backgroundColor: alpha(theme.palette.primary.main, 0.05),
    },
    focused: {
        backgroundColor: alpha(theme.palette.primary.main, 0.15),
    },
    labelScrolled: {
        backgroundColor: alpha(theme.palette.primary.main, 0.05),
        color: theme.palette.primary.main,
        fontWeight: "700!important",
    },
    advIconButton: {
        width: 24,
        height: 24,
        marginBottom: -2,
        "&:focus-visible": {
            outlineStyle: "solid",
            outlineOffset: 0,
        },
        [theme.breakpoints.down("md")]: {
            marginTop: -3,
        },
    },
    checkbox: {
        "& svg": {
            [theme.breakpoints.down("md")]: {
                fontSize: "1.22222223rem",
                marginTop: 1,
            },
        },
    },
    hvdIcon: {
        marginTop: 2,
        display: "flex",
        alignItems: "center",
        maxHeight: 20,
        position: "relative",
    },
}));

const CustomTreeItem = React.forwardRef(function CustomTreeItem(
    {
        id,
        disabled,
        children,
        nodes: { id: itemId },
        labelText: label,
        nodes: item,
        nodes: { type },
        selectedIndicators,
        onReselect,
        endContentComponent,
        endLabelComponent,
        onShowCertificate,
        onMultiSelect,
        onQuickNav,
        refs,
        ...other
    },
    ref
) {
    const {
        getRootProps,
        getContentProps,
        getIconContainerProps,
        getCheckboxProps,
        getLabelProps,
        getGroupTransitionProps,
        status,
    } = useTreeItem2({ id, itemId, children, label, disabled, rootRef: ref });

    const { focused, selected } = status;

    const stateRef = useRef(status);
    const observedRef = useRef();
    const rootRef = useRef();
    const classes = useStyles();
    const { t } = useTranslation();
    const lgUp = useMediaQuery((theme) => theme.breakpoints.up("lg"));

    const _checkboxAriaLabel = focused ? "" : label;

    const isParentSelected = !!children
        ? selectedIndicators.find((s) => {
              return item.id?.split("-")?.length === 1
                  ? s.uid.split("-").includes(item.id?.toString())
                  : selectedIndicators.find((s) => s.uid.includes(item.id));
          })
        : false;

    /** States */
    const [ACTIVE, setACTIVE] = useState(false);
    const [FREEZE, setFREEZE] = useState(false);
    /** - redux */
    const showIDs = useSelector(getShowIDs);
    const scrolled = useSelector(getScrolled);

    /** effects */
    /** - activate quick nav */
    useEffect(() => {
        if (!focused) return;
        setACTIVE(selected !== stateRef?.current?.selected);
    }, [selected, focused, stateRef]);

    /** - scroll to invisible by focus */
    useEffect(() => {
        if (!focused || !observedRef?.current || !lgUp) return;

        if ("IntersectionObserver" in window) {
            const root = document?.getElementById("indicators-tree");
            const observed = observedRef?.current;
            let config = {
                root,
                rootMargin: "0px",
                threshold: 0.5,
            };

            const onChange = (changes, observer) => {
                changes.forEach((change) => {
                    if (change.isIntersecting) {
                        observer.unobserve(change.target);
                    } else {
                        const top = (observed?.offsetTop || root.scrollTop) - 8;
                        root?.scrollTo && root.scrollTo({ top, behavior: "smooth" });
                    }
                });
            };

            let observer = new IntersectionObserver(onChange, config);
            observer.observe(observedRef?.current);
        }
    }, [focused, observedRef, lgUp]);

    /** Handlers */
    const handleOnShowCertificate = () => {
        onShowCertificate && onShowCertificate({ item });
    };

    const handleOnMultiSelect = (event) => {
        onMultiSelect && onMultiSelect({ anchorEl: event.currentTarget, item });
    };

    const handleOnQuickNav = (event) => {
        onQuickNav && onQuickNav({ anchorEl: event.currentTarget, item });
    };

    return (
        <TreeItem2Provider itemId={itemId}>
            <TreeItem2Root
                {...getRootProps(other)}
                className={children ? "" : clsx(classes.root, selected && classes.selected, focused && classes.focused)}
                ref={rootRef}
            >
                <Box sx={{ display: "flex", pr: 0.5 }} aria-label={label} tabIndex={-1}>
                    <CustomTreeItemContent
                        {...getContentProps()}
                        ref={observedRef}
                        className={clsx(
                            classes.content,
                            children ? clsx(selected && classes.selected, focused && classes.focused) : ""
                        )}
                        aria-hidden={FREEZE}
                        sx={{ position: "relative", alignItems: "flex-start" }}
                    >
                        <TreeItem2IconContainer {...getIconContainerProps()} sx={{ width: "auto" }}>
                            <TreeItem2Icon status={status} />
                        </TreeItem2IconContainer>

                        {type === "INDICATOR" ? (
                            <TreeItem2Checkbox
                                {...getCheckboxProps()}
                                ref={(ref) => {
                                    if (ref) {
                                        const _input = ref?.querySelector("input");
                                        if (_input) {
                                            _input?.setAttribute("aria-label", _checkboxAriaLabel);
                                        }
                                    }
                                }}
                                className={classes.checkbox}
                            />
                        ) : null}

                        {showIDs ? (
                            <Box
                                sx={{
                                    fontSize: "0.555555556rem",
                                    position: "absolute",
                                    left: 12,
                                    top: 10,
                                    transform: "translateX(-100%)",
                                }}
                            >
                                {item.indicator_id}
                            </Box>
                        ) : null}
                        <TreeItem2Label
                            {...getLabelProps()}
                            className={clsx(
                                classes.label,
                                isParentSelected && classes.labelAccent,
                                scrolled === item.id && classes.labelSelected,
                                selected && classes.labelSelected
                            )}
                        />
                    </CustomTreeItemContent>

                    <Silent rootRef={rootRef}>
                        {type === "INDICATOR" ? (
                            <>
                                <AppTooltip
                                    title={t("Otwórz metadane, w tym definicje")}
                                    arrow
                                    placement={"top"}
                                    type={"dark"}
                                    classes={{ tooltip: classes.tooltip }}
                                >
                                    <IconButton
                                        size={"small"}
                                        className={classes.advIconButton}
                                        onClick={handleOnShowCertificate}
                                        aria-label={t(
                                            "Otwórz metadane, w tym definicje dla {{label}}{{dot }} Kliknij klawisz Escape, aby wrócić do elementu drzewa - {{label}}",
                                            { label, dot: "." }
                                        )}
                                        onFocus={() => setFREEZE(true)}
                                        onBlur={() => setFREEZE(false)}
                                    >
                                        <InfoOutlined sx={{ color: "primary.main", fontSize: "1.11111111rem" }} />
                                    </IconButton>
                                </AppTooltip>
                            </>
                        ) : type === "GROUP" && item.children[0]?.type === "INDICATOR" ? (
                            <AppTooltip
                                title={t("Zaznacz / Odznacz wiele zmiennych")}
                                arrow
                                placement={"top"}
                                type={"dark"}
                                classes={{ tooltip: classes.tooltip }}
                            >
                                <IconButton
                                    size={"small"}
                                    className={classes.advIconButton}
                                    onClick={handleOnMultiSelect}
                                    aria-label={t(
                                        "Zaznacz / Odznacz wiele zmiennych dla {{label}}{{dot }} Kliknij klawisz Escape, aby wrócić do elementu drzewa - {{label}}",
                                        { label, dot: "." }
                                    )}
                                    onFocus={() => setFREEZE(true)}
                                    onBlur={() => setFREEZE(false)}
                                >
                                    <AddCircleOutline sx={{ color: "primary.main", fontSize: "1.11111111rem" }} />
                                </IconButton>
                            </AppTooltip>
                        ) : null}
                    </Silent>

                    {!!ACTIVE && type === "INDICATOR" && lgUp ? (
                        <>
                            <Box sx={{ flexGrow: 1 }} />
                            <Silent rootRef={rootRef}>
                                <AppTooltip
                                    title={t("Otwórz podmenu szybkich akcji")}
                                    arrow
                                    placement={"top"}
                                    type={"dark"}
                                    classes={{ tooltip: classes.tooltip }}
                                >
                                    <IconButton
                                        size={"small"}
                                        className={classes.advIconButton}
                                        sx={{ ml: 1 }}
                                        onClick={handleOnQuickNav}
                                        aria-label={t(
                                            "Otwórz podmenu szybkich akcji{{dot }} Kliknij klawisz Escape, aby wrócić do elementu drzewa - {{label}}",
                                            { label, dot: "." }
                                        )}
                                        onFocus={() => setFREEZE(true)}
                                        onBlur={() => setFREEZE(false)}
                                    >
                                        <MoreVert sx={{ color: "primary.main", fontSize: "1.11111111rem" }} />
                                    </IconButton>
                                </AppTooltip>
                            </Silent>
                        </>
                    ) : null}
                </Box>

                {children && <TreeItem2GroupTransition {...getGroupTransitionProps()} />}
            </TreeItem2Root>
        </TreeItem2Provider>
    );
});

export default CustomTreeItem;
